import { addTimeout } from "@/utils";
import { FILENAME_INDEX, SELECT_FOR_UPLOAD_INDEX } from "camera/lib/Recorder/DB";
import { DB_TIMEOUT, MAX_UPLOAD_QUEUE_BYTES } from "camera/lib/Recorder/const";
import Uploader from "./Uploader";
import { useStation } from "camera/store/station";
import { getIsPaired } from "camera/store/station/selectors";
import CrashReporter from "@/modules/app/CrashReporter";

type QueuedSegment = {
  dbKey: number | null;
  inMemory: { segment: HlsDataItem; init: HlsDataItem | null } | null;
};

export class MediaUploadManager {
  private static recorder: HlsRecorder | null;
  private static isUploading = false;

  private static segmentsToUpload: QueuedSegment[] = [];
  private static currentlyUploadingKeys = new Set<number>();
  private static uploadedInitFilesMap = new Map<string, boolean>();

  private static getCanProcess = () => getIsPaired(useStation.getState());

  static reset = () => {
    this.isUploading = false;

    this.segmentsToUpload = [];
    this.currentlyUploadingKeys.clear();
    this.uploadedInitFilesMap.clear();
  };

  static init = (recorderCreator: RecorderCreator) => {
    this.recorder = recorderCreator.recorder;
    this.recorder.on("segment-for-upload", this.addSegmentFromRecorder);

    const unsub = recorderCreator.onRecorderInit(() => {
      unsub();
      if (this.getCanProcess()) this.uploadPreviousSessionSegments();
    });
  };

  static getUnuploadedSegmentCount = () => this.segmentsToUpload.length;
  static hasMemoryOnlySegments = () => this.segmentsToUpload.some((queued) => !queued.dbKey);

  private static addSegmentFromRecorder = ({ segment, init, dbKey }: UploadableSegment) => {
    if (dbKey) this.currentlyUploadingKeys.add(dbKey);
    this.segmentsToUpload.push({ dbKey, inMemory: { segment, init } });
    this.limitMemoryUse();
    if (!this.isUploading) this.upload();
  };

  private static limitMemoryUse = () => {
    let bytesInMemory = 0;
    const newestFirst = [...this.segmentsToUpload].reverse();
    for (const queued of newestFirst) {
      if (!queued.inMemory) continue;
      bytesInMemory += queued.inMemory.segment.data.byteLength;
      if (bytesInMemory <= MAX_UPLOAD_QUEUE_BYTES) continue;

      const { filename } = queued.inMemory.segment;
      if (queued.dbKey) {
        log.upload(`Upload memory limit reached, ${filename} will be read from the DB`);
        queued.inMemory = null;
      } else {
        log.err(`Upload memory limit reached and ${filename} isn't stored in the DB, dropping it`);
        this.removeFromQueue(queued);
      }
    }
  };

  private static markAsUploaded = async (segment: HlsDataItem, dbKey: number) => {
    if (!this.recorder) return;
    const db = this.recorder.db;
    segment.uploaded = 1;
    segment.selectForUpload = 0;
    await addTimeout(() => db.getWrite<HlsDataItem>("hls-data")(segment, dbKey), DB_TIMEOUT).catch((err) =>
      log.err(`Couldn't mark ${segment.filename} as uploaded`, err)
    );
  };

  static isRecording = () => this.recorder?.status === "recording";

  private static readSegment = async (key: number) => {
    if (!this.recorder) return null;
    try {
      const segment = await this.recorder.db.readByKey<HlsDataItem | null>("hls-data", key);
      if (segment !== null) return segment ?? null;
    } catch (err) {
      log.err(`Couldn't read hls data file ${key}`, err);
    }

    await this.recorder.db.deleteByKeys("hls-data", [key]);
    return null;
  };

  private static getSegment = async ({ inMemory, dbKey }: QueuedSegment) => {
    if (inMemory) return inMemory.segment;
    if (!dbKey) return null;
    return addTimeout(() => this.readSegment(dbKey), DB_TIMEOUT).catch(() => "DB_NOT_ANSWERING" as const);
  };

  private static uploadInitFileIfNotUploaded = async (segment: HlsDataItem, init?: HlsDataItem | null) => {
    if (!this.recorder) return;
    const db = this.recorder.db;
    const { initFilename } = segment;

    if (!initFilename) {
      log.err(`'initFilename' not found on segment ${segment.filename}`);
      return "NO_INIT_FOUND";
    }
    if (this.uploadedInitFilesMap.has(initFilename)) return;

    const initFile =
      init ??
      (await addTimeout(() => db.getRead<HlsDataItem | null>("hls-data", "filename")(initFilename), DB_TIMEOUT));

    if (!initFile) {
      log.err(`Init file ${initFilename} was not found for file ${segment.filename}`);
      return "NO_INIT_FOUND";
    }

    if (initFile.uploaded === 1) log.warn(`init file ${initFile.filename} already uploaded, uploading again`);
    log.upload("About to upload init", initFile.filename);
    const result = await this.uploadInit(initFile);
    if (result === "REJECTED") return "REJECTED";

    this.uploadedInitFilesMap.set(initFilename, true);
    this.markInitAsUploaded(initFilename);
  };

  private static markInitAsUploaded = (initFilename: string) => {
    if (!this.recorder) return;
    try {
      const keyRange = IDBKeyRange.only(initFilename);
      const objectStore = this.recorder.db.createTransaction("readwrite", "hls-data");
      const index = objectStore.index(FILENAME_INDEX);
      const request = index.openCursor(keyRange);

      request.onsuccess = async (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const file = cursor.value as HlsDataItem | null;
          if (!file) {
            log.err(
              "Invalid cursor value for hls data file in uploadInitFileIfNotUploaded, deleting key",
              cursor.primaryKey
            );
            cursor.delete();
            cursor.continue();
            return;
          }
          file.uploaded = 1;
          log.upload("About to update init file in DB", file);
          cursor.update(file);
        }
      };
    } catch (err) {
      log.err(`Couldn't mark init ${initFilename} as uploaded`, err);
    }
  };

  private static upload = () => {
    if (this.isUploading) return;
    this.isUploading = true;

    const next = async () => {
      try {
        let isDbAnswering = true;
        const toUpload = [...this.segmentsToUpload];
        log.upload("Found pending uploads", toUpload.length);
        for (const queued of toUpload) {
          if (!this.recorder) {
            this.isUploading = false;
            return;
          }
          const { dbKey } = queued;
          const init = queued.inMemory?.init ?? null;
          const isOnlyInDb = !queued.inMemory;
          if (isOnlyInDb && !isDbAnswering) continue;

          const segment = await this.getSegment(queued);
          if (segment === "DB_NOT_ANSWERING") {
            isDbAnswering = false;
            continue;
          }
          if (!segment || segment.selectForUpload !== 1) {
            this.removeFromQueue(queued);
            continue;
          }

          const initResult = await this.uploadInitFileIfNotUploaded(segment, init);
          if (initResult === "NO_INIT_FOUND") {
            log.warn(`About to delete file ${segment.filename}`);
            if (dbKey) {
              await addTimeout(() => this.recorder!.db.deleteByKeys("hls-data", [dbKey]), DB_TIMEOUT).catch(() => {});
            }
            this.removeFromQueue(queued);
            continue;
          }

          log.upload("About to upload segment", segment.filename);
          const result = initResult === "REJECTED" ? initResult : await this.uploadSegment(segment, init);
          if (result === "REJECTED") {
            this.moveToEndOfQueue(queued);
            continue;
          }

          if (dbKey) await this.markAsUploaded(segment, dbKey);
          this.removeFromQueue(queued);
        }
        const hasSegmentsInMemory = this.segmentsToUpload.some((queued) => queued.inMemory);
        const hasSegmentsOnlyInDb = this.segmentsToUpload.some((queued) => !queued.inMemory);
        if (hasSegmentsInMemory || (hasSegmentsOnlyInDb && isDbAnswering)) next();
        else this.isUploading = false;
      } catch (err: any) {
        log.err("Encoutered client error while uploading", err);
        CrashReporter.sendException("upload' failed", { key: "err", extra: err?.message || err });
        this.isUploading = false;
      }
    };

    next();
  };

  private static uploadPreviousSessionSegments = async () => {
    if (!this.recorder) return;
    log.upload("'uploadPreviousSessionSegments' called");

    try {
      const db = this.recorder.db;
      const pendingKeys = await addTimeout(() => db.readIndexKeys("hls-data", SELECT_FOR_UPLOAD_INDEX, 1), DB_TIMEOUT);
      log.upload("Prepared all segments");
      this.addUniqueItemsToUploadQueue(pendingKeys.map(({ primaryKey }) => primaryKey as number));
      if (!this.isUploading) this.upload();
    } catch (err) {
      log.err("Error while preparing segments", err);
    }
  };

  static startMarkingDataForUpload = () => {
    if (!this.recorder) return;
    log.upload("Called 'startMarkingDataForUpload'");
    this.recorder.startMarkingDataForUpload();
  };

  static stopMarkingDataForUpload = () => {
    if (!this.recorder) return;
    log.upload("Called 'stopMarkingDataForUpload'");
    this.recorder.stopMarkingDataForUpload();
  };

  private static uploadInit = async (file: HlsDataItem) => {
    if (!file.initId) {
      log.err("Couldnt find ID of init file", file.filename);
      return;
    }
    const blob = new Blob([file.data], { type: "video/mp2t" });
    const payload = new FormData();

    payload.append("contentType", "VIDEO");
    payload.append("type", "INIT");
    payload.append("initId", String(file.initId));
    payload.append("imageRotation", "ROTATION_0");
    payload.append("file", blob, file.filename);

    return await Uploader.upload(payload, file.filename);
  };

  private static uploadSegment = async (file: HlsDataItem, init?: HlsDataItem | null) => {
    if (!this.recorder) return;

    if (!file.initId) {
      log.err("Couldnt find ID of init file", file.initFilename);
      CrashReporter.sendMessage(`Couldnt find ID of init file ${file.initFilename} for ${file.filename}`);
      return;
    }

    const blob = new Blob([file.data], { type: "video/mp2t" });
    const payload = new FormData();

    payload.append("contentType", "VIDEO");
    payload.append("type", "DATA");
    payload.append("initId", String(file.initId));
    payload.append("discontinuity", String(file.discontinuity));
    payload.append("date", String(new Date(file.createdAt).getTime()));
    payload.append("duration", String(Math.round(parseFloat(file.duration!) * 1000)));
    payload.append("file", blob, file.filename);

    let response = await Uploader.upload(payload, file.filename);
    if (response === "INIT_SEGMENT_NOT_FOUND") {
      log.err(`Init for ${file.filename} not found on the server, uploading it again`);
      if (file.initFilename) this.uploadedInitFilesMap.delete(file.initFilename);
      const initResult = await this.uploadInitFileIfNotUploaded(file, init);
      if (initResult === "REJECTED") return initResult;
      if (initResult !== "NO_INIT_FOUND") response = await Uploader.upload(payload, file.filename);
    }
    if (response === "INIT_SEGMENT_NOT_FOUND") log.err(`Init for ${file.filename} still not found, skipping the file`);
    if (response === "REJECTED") return response;
  };

  private static addUniqueItemsToUploadQueue = (keys: number[]) => {
    keys.forEach((key) => {
      if (this.currentlyUploadingKeys.has(key)) return;
      this.currentlyUploadingKeys.add(key);
      this.segmentsToUpload.push({ dbKey: key, inMemory: null });
    });
  };

  private static removeFromQueue = (queued: QueuedSegment) => {
    this.segmentsToUpload = this.segmentsToUpload.filter((q) => q !== queued);
    if (queued.dbKey) this.currentlyUploadingKeys.delete(queued.dbKey);
  };

  private static moveToEndOfQueue = (queued: QueuedSegment) => {
    this.segmentsToUpload = [...this.segmentsToUpload.filter((q) => q !== queued), queued];
  };
}
